import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ResourceModalShell from './ResourceModalShell';
import { faqCategories } from '../content/faqContent';

const HelpCenterModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load last viewed tab
  useEffect(() => {
    const lastTab = localStorage.getItem('faq_last_tab');
    if (lastTab && faqCategories.find(c => c.id === lastTab)) {
      setSelectedCategory(lastTab);
    }
  }, []);

  // Save selected tab
  useEffect(() => {
    if (selectedCategory) {
      localStorage.setItem('faq_last_tab', selectedCategory);
    }
  }, [selectedCategory]);

  // Filter FAQs based on search
  const getFilteredFaqs = () => {
    if (!searchQuery.trim()) {
      return faqCategories.find(c => c.id === selectedCategory)?.faqs || [];
    }

    const query = searchQuery.toLowerCase();
    const allFaqs = faqCategories.flatMap(cat => 
      cat.faqs.map(faq => ({ ...faq, category: cat.label }))
    );

    return allFaqs.filter(faq => 
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query)
    );
  };

  const filteredFaqs = getFilteredFaqs();

  const handleCtaClick = (route) => {
    onClose();
    navigate(route);
  };

  return (
    <ResourceModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Help Center"
      subtitle="FAQs & Support"
    >
      <div style={{
        display: 'flex',
        height: '100%',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden'
      }}>
        {/* Sidebar / Tab Selector */}
        {!isMobile ? (
          <div style={{
            width: '240px',
            borderRight: '1px solid #E5E7EB',
            padding: '16px',
            overflowY: 'auto'
          }}>
            {faqCategories.map(category => (
              <button
                key={category.id}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setSearchQuery('');
                  setExpandedFaq(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '4px',
                  background: selectedCategory === category.id ? '#EFF6FF' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: selectedCategory === category.id ? '600' : '400',
                  color: selectedCategory === category.id ? '#3B82F6' : '#374151',
                  transition: 'background 0.15s'
                }}
                onMouseOver={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.background = '#F9FAFB';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '16px' }}>{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSearchQuery('');
                setExpandedFaq(null);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              {faqCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Content Area */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0
        }}>
          {/* Search Bar */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search
                size={18}
                color="#9CA3AF"
                style={{
                  position: 'absolute',
                  left: '12px'
                }}
              />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* FAQ List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            padding: '16px',
            minHeight: 0
          }}>
            {filteredFaqs.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#9CA3AF'
              }}>
                <p style={{ margin: 0 }}>No results found for \"{searchQuery}\"</p>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: expandedFaq === index ? '#F9FAFB' : '#FFFFFF',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#0A0A0A',
                      flex: 1
                    }}>
                      {faq.question}
                    </span>
                    {expandedFaq === index ? (
                      <ChevronUp size={18} color="#6B7280" />
                    ) : (
                      <ChevronDown size={18} color="#6B7280" />
                    )}
                  </button>
                  
                  {expandedFaq === index && (
                    <div style={{
                      padding: '16px',
                      background: '#F9FAFB',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      <p style={{
                        margin: '0 0 12px 0',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#374151'
                      }}>
                        {faq.answer}
                      </p>
                      {faq.cta && (
                        <button
                          onClick={() => handleCtaClick(faq.cta.route)}
                          style={{
                            padding: '8px 16px',
                            background: '#3B82F6',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {faq.cta.label}
                          <ExternalLink size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </ResourceModalShell>
  );
};

export default HelpCenterModal;
